import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ComponentModel } from 'src/app/models/maintenance/component.model';
import { ElementModel } from 'src/app/models/maintenance/element.model';
import { ElementService } from 'src/app/services/maintenance/element.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-element',
  templateUrl: './register-element.component.html',
  styleUrls: ['./register-element.component.scss'],
  standalone: false
})
export class RegisterElementComponent implements OnInit {
  public config = inject(DynamicDialogConfig);
  private readonly elementService = inject(ElementService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly toastHandlerService = inject(ToastHandlerService);

  componentContract: ComponentModel = new ComponentModel();
  elementContract: ElementModel = new ElementModel();
  elementId: number = 0;
  formElement: FormGroup = new FormGroup({});
  suscription: Subscription;


  ngOnInit() {
    this.validateForm(this.elementContract);
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }

    this.suscription = input$?.subscribe((inputs: any) => {
      this.elementContract = inputs.elementModel;
      this.componentContract = inputs.componentModel;
    });
    this.elementId = this.elementContract.elementId;
    this.handleElement();
  }

  async changeCode(code: number) {
    if (!code || this.elementContract.code === code) { return; }
    try {
      const response = await firstValueFrom(this.elementService.getElementByCode(this.componentContract.maintenanceRoutineId, this.componentContract.componentId, code));
      this.elementId = response.componentId;
      this.elementContract = response;
      this.validateForm(this.elementContract);
    } catch (error) {
      console.error('Error al buscar el elemento por código.', error);
    }
  }

  createNewRegister() {
    this.elementId = 0;
    this.elementContract = new ElementModel();
    this.elementContract.componentId = this.componentContract.componentId;
    this.validateForm(this.elementContract);
  }

  async getElementById() {
    try {
      const response = await firstValueFrom(this.elementService.getElementById(this.elementId));
      this.elementContract = response;
      this.validateForm(response);
    } catch (error) {
      console.error('Error al obtener el elemento por id.', error);
    }
  }

  async handleElement() {
    if (this.elementId !== null && this.elementId !== 0) {
      await this.getElementById();
    } else {
      this.createNewRegister();
    }
  }

  onReloadEmit() {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'onReload', payload: true });
  }

  async send() {
    this.validateFormControls();
    if (this.formElement.valid) {
      const modelElement = this.formElement.getRawValue() as ElementModel;
      if (this.elementId == 0) {
        try {
          const response = await firstValueFrom(this.elementService.createElement(modelElement));
          this.elementId = response.elementId;
          this.elementContract = response;
          this.validateForm(this.elementContract);
          this.toastHandlerService.showToast('success', `El elemento fue creado exitosamente.`);
        } catch (error) {
          console.error('Error al crear el elemento.', error);
        }
      } else {
        this.elementContract.name = modelElement.name;
        try {
          const response = await firstValueFrom(this.elementService.updateElement(this.elementContract));
          this.elementContract = response;
          this.validateForm(this.elementContract);
          this.toastHandlerService.showToast('success', `El elemento fue modificado exitosamente.`);
        } catch (error) {
          console.error('Error al actualizar el elemento.', error);
        }
      }
      this.onReloadEmit();
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: any = {
      name: {
        required: 'El campo nombre es obligatorio.',
        maxlength: 'El campo nombre no puede tener más de 80 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formElement.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(element: ElementModel) {
    this.formElement = this.formBuilder.group({
      componentId: [this.componentContract.componentId],
      maintenanceRoutineId: [this.componentContract.maintenanceRoutineId],
      code: [{ value: element.code, disabled: this.elementId === 0 }, [Validators.required]],
      name: [element.name, [Validators.required, Validators.maxLength(80)]],
    });
  }

  validateFormControls() {
    Object.keys(this.formElement.controls).forEach((key) => {
      const control = this.formElement.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
